import { useEffect, useState } from "react";
import { Languages } from "lucide-react";

import {
  LOCALE_BADGE,
  SUPPORTED_LOCALES,
  isLocalized,
  toLocalized,
  type DisplayLabel,
  type Locale,
  type LocalizedString,
} from "@pervasivemind/dashboard-render";
import { cn } from "@/lib/utils";
import { useT } from "@/i18n";
import { translationsApi } from "@/lib/translations-api";

interface BilingualInputProps {
  /** Current value — accepts the legacy ``string`` OR ``LocalizedString``. */
  value: DisplayLabel;
  /** Called with the new ``LocalizedString`` after every keystroke. */
  onChange: (next: LocalizedString) => void;
  placeholder?: string;
  /** Used by the outer ``<Field>`` ``htmlFor`` linkage. Becomes the first locale's input id. */
  id?: string;
  /** When true, renders a ``<textarea>`` instead of ``<input>``. */
  multiline?: boolean;
  /** Extra Tailwind classes applied to each input/textarea. */
  className?: string;
  /** Forwarded to both inputs. Useful for ``type="number"`` etc. */
  type?: string;
  /** Disable both inputs. */
  disabled?: boolean;
  /**
   * Locales to surface as side-by-side editors. Defaults to
   * ``SUPPORTED_LOCALES`` (currently ``["en", "es", "pt"]``). Pass an
   * override only when a specific call site needs a tighter or
   * broader set — most callers should rely on the default so a future
   * project-wide locale addition picks them up for free.
   */
  locales?: ReadonlyArray<Locale>;
}

/**
 * Side-by-side editor for a single localized display string —
 * extensible to N locales.
 *
 * Iter 16 introduced bilingual labels (`{en, es}`). The component used
 * to hard-code two slots; the 2026-05-18 audit revisit widened it to
 * loop over ``SUPPORTED_LOCALES`` so adding a new locale (``pt``,
 * ``fr``, ...) is a one-line change in ``i18n-resolve.ts``. The
 * default 2-locale case still renders as a 2-column grid; 3+ locales
 * stack into a responsive grid (1 col narrow, 2 cols wider).
 *
 * Backward compat: when ``value`` is a plain ``string`` (legacy
 * single-language), the component normalizes it via ``toLocalized``
 * — the admin sees the same value in every locale slot until they
 * differentiate. An amber ring marks the input until the first
 * keystroke commits the bilingual shape via ``onChange``.
 */
export function BilingualInput({
  value,
  onChange,
  placeholder,
  id,
  multiline,
  className,
  type = "text",
  disabled,
  locales,
}: BilingualInputProps): JSX.Element {
  const activeLocales = locales ?? SUPPORTED_LOCALES;

  // Local mirror so typing stays responsive even before the parent
  // re-renders. The ``useEffect`` re-syncs when ``value`` changes
  // externally (e.g. when a different draft loads into the editor).
  const [local, setLocal] = useState<LocalizedString>(() => toLocalized(value));

  useEffect(() => {
    setLocal(toLocalized(value));
  }, [value]);

  const update = (lang: Locale, next: string): void => {
    const merged: LocalizedString = { ...local, [lang]: next };
    setLocal(merged);
    onChange(merged);
  };

  // PT plan, phase 4: fill an empty PT from EN/ES through the portal's
  // model. The answer is a translation or an error shown under the
  // field, never a copy of the Spanish text.
  const { t } = useT();
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState<string | null>(null);
  const canTranslate =
    !disabled && !(local.pt ?? "").trim() && Boolean((local.en ?? "").trim() || (local.es ?? "").trim());
  const translateToPt = async (): Promise<void> => {
    setTranslating(true);
    setTranslateError(null);
    try {
      const out = await translationsApi.translate("pt", {
        text: { en: local.en ?? "", es: local.es ?? "" },
      });
      const pt = out.translations.text;
      if (!pt) throw new Error("empty translation");
      update("pt", pt);
    } catch (err) {
      setTranslateError(err instanceof Error ? err.message : String(err));
    } finally {
      setTranslating(false);
    }
  };

  const inputCls = cn(
    "input flex-1 min-w-0",
    // Amber ring while the value is still a legacy plain string — a
    // gentle cue that one keystroke graduates it to the per-locale
    // shape.
    !isLocalized(value) && typeof value === "string" && value
      ? "ring-1 ring-warning/40"
      : "",
    className,
  );

  // Render input vs textarea explicitly — TS narrows the prop bag per
  // branch, which is what we want (``rows`` belongs to textarea only).
  const renderField = (lang: Locale, isFirst: boolean) =>
    multiline ? (
      <textarea
        id={isFirst ? id : undefined}
        value={local[lang] ?? ""}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => update(lang, e.target.value)}
        className={inputCls}
        rows={2}
      />
    ) : (
      <input
        id={isFirst ? id : undefined}
        type={type}
        value={local[lang] ?? ""}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => update(lang, e.target.value)}
        className={inputCls}
      />
    );

  // Two locales fit the historical 2-col layout perfectly. 3+ locales
  // step up to a 2-col grid that wraps; on narrow viewports any count
  // stacks vertically. The 1-col fallback (only 1 locale active)
  // simply renders a single row.
  const gridCols =
    activeLocales.length === 1
      ? "grid-cols-1"
      : activeLocales.length === 2
        ? "grid-cols-1 sm:grid-cols-2"
        : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";

  return (
    <div className={cn("grid gap-1 sm:gap-2", gridCols)}>
      {activeLocales.map((lang, i) => (
        <div key={lang} className="flex items-stretch gap-1">
          <span
            // El chip EN/ES mide 4.35:1 con neutral-500 SOBRE neutral-100:
            // el paso 500 llega a 4.74 sobre blanco, pero el fondo del
            // chip le come la diferencia. neutral-600 da 7.1.
            className="flex shrink-0 items-center rounded-md bg-neutral-100 px-1.5 text-xs font-semibold text-neutral-600"
            title={lang}
          >
            {LOCALE_BADGE[lang] ?? lang.toUpperCase()}
          </span>
          {renderField(lang, i === 0)}
          {lang === "pt" && canTranslate && (
            <button
              type="button"
              onClick={() => void translateToPt()}
              disabled={translating}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-neutral-200 px-2 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
              title={t("editor.translatePt.button")}
            >
              <Languages size={13} aria-hidden="true" />
              {translating ? t("editor.translatePt.busy") : t("editor.translatePt.button")}
            </button>
          )}
        </div>
      ))}
      {translateError !== null && (
        <p role="alert" className="text-xs text-error sm:col-span-full">
          {t("editor.translatePt.error", { message: translateError })}
        </p>
      )}
    </div>
  );
}
